import {
  IMPORT_PROJECT,
  MODAL_SAVE_AS_TEMPLATE,
  MODAL_UPGRADE_PLAN,
} from '@components/App/Modal/constants'
import { useViewMenu } from '@components/ProjectViewContent'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { ProjectAvatarActions } from '@store/projectAvatar'
import { artBoardActions } from '@store/reducers/artboard'
import { ProjectActions } from '@store/reducers/project'
import { openModal } from '@storeOld/reducers/ui'
import type { RefObject } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { createSwitch } from '@/utils'
import { useExportMenu } from './useExportMenu'

const ActivityMode = artBoardActions.changeMode('activity_history')

const StorageMode = artBoardActions.changeMode('storage')

const ExportUpgradeModal = openModal({
  modalType: MODAL_UPGRADE_PLAN,
  modalProps: {
    slideType: 'export',
  },
})

export const useProjectActions = (
  input: RefObject<HTMLInputElement | null>
) => {
  const dispatch = useDispatch()
  const projectId = useSelector(migrationProjectSelector.getProjectId) as string
  const { onExport } = useExportMenu()
  const { onViewClick } = useViewMenu()

  const switcher = createSwitch()
    .case(['file', 'export', 'upgrade'], () => dispatch(ExportUpgradeModal))
    .case(['file', 'export'], (type) => onExport(type))
    .case(['file', 'save_as_template'], () =>
      dispatch(openModal(MODAL_SAVE_AS_TEMPLATE))
    )
    .case(['file', 'save_as_template_upgrade'], () => {
      sendGtmEvent(
        GtmTriggerName.TEMPLATE_UPGRADE_CLICK,
        'Upgrade from File - Save as Template'
      )

      dispatch(
        openModal({
          modalType: MODAL_UPGRADE_PLAN,
          modalProps: { planType: 'pro', slideType: 'templates' },
        })
      )
    })
    .case(['file', 'import'], () => {
      dispatch(
        // @ts-expect-error - openModal lives in untyped legacy js
        openModal(IMPORT_PROJECT, {
          isLocked: true,
          intoOpenProject: true,
        })
      )
    })
    .case(['file', 'move'], (workspaceId, folderId) =>
      dispatch(
        ProjectActions.moveToWorkspace({ id: projectId, folderId, workspaceId })
      )
    )
    .case(['activity_history'], () => dispatch(ActivityMode))
    .case(['activity_upgrade'], () => {
      sendGtmEvent(GtmTriggerName.UPGRADE_HISTORY)
      dispatch(openModal(MODAL_UPGRADE_PLAN))
    })
    .case(['attached_files'], () => dispatch(StorageMode))
    .case(['structure'], () =>
      dispatch(artBoardActions.showStructure({ showStructure: true }))
    )
    .case(['view'], (...rest) => onViewClick(rest))
    .case(['project_icon', 'default'], () =>
      dispatch(ProjectAvatarActions.reset())
    )
    .case(['project_icon', 'upgrade'], () =>
      dispatch(openModal(MODAL_UPGRADE_PLAN))
    )
    .case(['project_icon', 'upload'], () => input.current?.click())

  const onClick = (keys: string[]) => switcher.run(keys)

  return { onClick }
}
